import {
    istLeererBlocktext,
    notizSchluessel,
    type Block,
    type BlockNotiz,
    type NotizQuelle,
} from "./blockModell";

/**
 * Kommentare am Block und an der Ereigniskarte (PO 13./14.09.2026) — REINE
 * Ableitung, bewusst ohne React und ohne Fetch, damit der node-Runner sie
 * direkt prüft.
 *
 * ══ EIN KOMMENTAR IST EINE NOTIZ MIT BEZUG ═════════════════════════════════
 * KMVS hat keine Kommentartabelle. Ein Kommentar ist eine Zeile in
 * `VorgangNote` bzw. `TodoNote`, die sagt, wozu sie gehört:
 *
 *   · `parentId`     → Kommentar an einer NOTIZ
 *   · `mailLinkId`   → Kommentar an einer MAIL
 *   · `callLogId`    → Kommentar an einem ANRUF
 *   · `documentId`   → Kommentar an einem DOKUMENT
 *   · `terminId`     → Kommentar an einem TERMIN (PO 20.09.2026)
 *
 * Höchstens einer der vier Verlaufsbezüge ist gesetzt; das erzwingt das
 * Backend (`einVerlaufsBezug`). Deshalb braucht Feature „Kommentare" weder
 * eine Entity noch einen Endpoint noch eine Migration — nur diese Ableitung.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ DER FADEN IST FLACH ════════════════════════════════════════════════════
 * Wie in Word: an einem Element hängt EIN Faden. Wer antwortet, schreibt in
 * denselben Faden — der Beitrag bekommt dasselbe Ziel, nicht den Vorgänger als
 * Elternteil. Was aus dem alten V2-Chat an Antworten AUF Kommentare im Bestand
 * liegt, bleibt darum sichtbar im Strom stehen (siehe
 * {@link versteckteKommentare}) statt in einem Faden zu verschwinden, den
 * niemand öffnen kann.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Worauf ein Kommentar zeigen kann. */
export type KommentarZielArt = "notiz" | "mail" | "anruf" | "dokument" | "termin" | "entwurf";

/** Das kommentierte Element. */
export interface KommentarZiel {
    art: KommentarZielArt;
    /**
     * Bei `"notiz"` die Id der Elternnotiz, sonst die Id des
     * Verlaufseintrags (`MailLink.id`, `CallLog.id`, `Document.id`).
     */
    id: number;
    /**
     * NUR bei `"notiz"`: aus welcher Tabelle die Elternnotiz stammt.
     * `VorgangNote.parentId` zeigt auf eine VorgangNote, `TodoNote.parentId`
     * auf eine TodoNote — beide Tabellen haben eigene Id-Folgen, es gibt
     * `vorgang:5` und `todo:5` nebeneinander.
     */
    quelle?: NotizQuelle;
    /**
     * Der Arbeitsstrang, in dem ein NEUER Kommentar landen soll; null = am
     * Vorgang. Er geht beim Anlegen als `todoId` mit — sonst stünde der
     * Kommentar im falschen Strang (so macht es schon
     * `VorgangDetailPageV3.onEintragKommentar`).
     */
    todoId?: number | null;
    /**
     * NUR bei `"entwurf"` (User 22.10.2026): die Graph-Id des Mail-Entwurfs —
     * er hat noch keinen `MailLink`, also keine Zahl-Id (`id` ist dann 0).
     */
    entwurfId?: string;
}

/** Ein Beitrag im Faden. */
export interface Kommentar {
    /** Stabiler React-Schlüssel über beide Herkunftstabellen. */
    key: string;
    id: number;
    quelle: NotizQuelle;
    /** Ersteller; entscheidet über das Bearbeitungsrecht. */
    autor: string;
    /** Inhalt — HTML, wenn `istHtml`, sonst Klartext. */
    html: string;
    istHtml: boolean;
    angelegt: Date;
    /**
     * Der Haken (PO 18.09.2026); null = offen. Abhaken darf jeder, der den
     * Vorgang sieht — gebeten wird ja der Erwähnte, und der ist nicht
     * zwangsläufig Bearbeiter (PO 20.09.2026).
     */
    erledigtAm: Date | null;
    erledigtVon: string | null;
    /** Die markierte Textstelle, an der er hängt; null = die ganze Notiz. */
    anker: string | null;
}

const zeit = (iso?: string | null): Date => {
    if (!iso) return new Date(0);
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

/** Trägt die Notiz einen Bezug — ist sie also ein Kommentar? */
export function istKommentar(n: BlockNotiz): boolean {
    return zielVon(n) !== null;
}

/**
 * Worauf sich eine Notiz bezieht; null = sie bezieht sich auf nichts und ist
 * ein gewöhnlicher Block.
 *
 * Die Reihenfolge — Antwort vor Mail vor Anruf vor Dokument — ist willkürlich,
 * aber fest, damit dieselbe Zeile immer dasselbe Ziel ergibt. In der Praxis
 * überschneiden sich die Felder nicht: der Kommentarpfad aus V3 setzte nur
 * einen Verlaufsbezug, der Chat aus V2 nur `parentId`.
 */
export function zielVon(n: BlockNotiz): KommentarZiel | null {
    const todoId = n.todoId ?? null;
    if (n.parentId != null) {
        return { art: "notiz", id: n.parentId, quelle: n.source ?? "vorgang", todoId };
    }
    if (n.mailLinkId != null) return { art: "mail", id: n.mailLinkId, todoId };
    if (n.callLogId != null) return { art: "anruf", id: n.callLogId, todoId };
    if (n.documentId != null) return { art: "dokument", id: n.documentId, todoId };
    if (n.terminId != null) return { art: "termin", id: n.terminId, todoId };
    if (n.entwurfId) return { art: "entwurf", id: 0, entwurfId: n.entwurfId, todoId };
    return null;
}

/**
 * Der Vergleichsschlüssel eines Ziels. Bei einer Notiz steht die QUELLE darin
 * — ohne sie fiele der Kommentar an `todo:5` mit dem an `vorgang:5` zusammen.
 */
export function zielSchluessel(ziel: KommentarZiel): string {
    if (ziel.art === "notiz") return `notiz:${ziel.quelle ?? "vorgang"}:${ziel.id}`;
    if (ziel.art === "entwurf") return `entwurf:${ziel.entwurfId ?? ""}`;
    return `${ziel.art}:${ziel.id}`;
}

/** Das Ziel eines gezeichneten Blocks — sein Faden. */
export function zielFuerBlock(block: Block, todoId: number | null): KommentarZiel {
    return { art: "notiz", id: block.id, quelle: block.source, todoId };
}

/** Eine Notizzeile als Beitrag im Faden. */
function beitragAus(n: BlockNotiz): Kommentar {
    return {
        key: notizSchluessel(n),
        id: n.id,
        quelle: n.source ?? "vorgang",
        autor: n.author,
        html: n.text ?? "",
        istHtml: n.format === "html",
        angelegt: zeit(n.timestamp),
        // `zeit()` macht aus einem fehlenden Wert den 1.1.1970 — hier braucht
        // es echtes `null`, sonst gälte jeder offene Kommentar als erledigt.
        erledigtAm: n.erledigtAm ? zeit(n.erledigtAm) : null,
        erledigtVon: n.erledigtVon ?? null,
        anker: n.anker ?? null,
    };
}

/**
 * ALLE Fäden auf einmal, geschlüsselt nach {@link zielSchluessel}, jeder mit
 * dem ÄLTESTEN Beitrag zuerst — die Leserichtung eines Gesprächs.
 * Inhaltsleere Zeilen fallen weg, wie überall seit PO 13.09.2026
 * („leergeräumt heißt gelöscht").
 *
 * Auf einen Griff, weil die Notizfläche einmal JE BLOCK fragt: bei 200 Notizen
 * und 50 Blöcken wären das sonst 10.000 Durchläufe bei jedem Rendern.
 */
export function kommentareJeZiel(alle: BlockNotiz[]): Map<string, Kommentar[]> {
    const out = new Map<string, Kommentar[]>();
    for (const n of alle) {
        const z = zielVon(n);
        if (!z) continue;
        if (istLeererBlocktext(n.text)) continue;
        const schluessel = zielSchluessel(z);
        const liste = out.get(schluessel);
        if (liste) liste.push(beitragAus(n));
        else out.set(schluessel, [beitragAus(n)]);
    }
    for (const liste of out.values()) {
        liste.sort((a, b) => a.angelegt.getTime() - b.angelegt.getTime() || a.id - b.id);
    }
    return out;
}

/**
 * Ein Beitrag als KLARTEXT — was beim Ändern im Textfeld stehen soll.
 *
 * Der Faden schreibt und liest Klartext; das Feld ist ein `textarea`. Ein
 * Beitrag aus dem Bestand kann trotzdem Markup tragen (`format: 'html'`, etwa
 * ein Block, der später zum Kommentar wurde). Wer den unverändert ins Feld
 * legte, bekäme beim Ändern `<div>`-Hüllen zu sehen und speicherte sie
 * anschließend als sichtbaren Text mit.
 *
 * Umgewandelt wird deshalb HIER und nicht im DOM: die Ableitung läuft im
 * node-Runner mit, und `blockHtml.ts` brächte DOMPurify und damit ein DOM mit.
 * Zeilentrenner (`<br>`, `</div>`, `</p>`) werden zu Umbrüchen, alles andere
 * fällt weg — die fünf Entitäten, die ein `contentEditable` erzeugt, kommen
 * zurück.
 */
export function alsKlartext(k: Pick<Kommentar, "html" | "istHtml">): string {
    if (!k.istHtml) return k.html;
    return k.html
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(div|p|li)>/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/gi, "&")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

/**
 * Die Fäden an MARKIERTEN TEXTSTELLEN, geschlüsselt nach ihrem Anker
 * (PO 18.09.2026). Nur Kommentare an einer NOTIZ können einen Anker tragen;
 * ein Kommentar an einer Mail bezieht sich auf die ganze Mail.
 *
 * Ein Kommentar ohne Anker gehört zur ganzen Notiz und steht hinter der
 * Sprechblase des Abschnitts — er taucht hier bewusst nicht auf.
 */
export function kommentareJeAnker(alle: BlockNotiz[]): Map<string, Kommentar[]> {
    const out = new Map<string, Kommentar[]>();
    for (const n of alle) {
        if (!n.anker) continue;
        if (!istKommentar(n)) continue;
        if (istLeererBlocktext(n.text)) continue;
        const liste = out.get(n.anker);
        if (liste) liste.push(beitragAus(n));
        else out.set(n.anker, [beitragAus(n)]);
    }
    for (const liste of out.values()) {
        liste.sort((a, b) => a.angelegt.getTime() - b.angelegt.getTime() || a.id - b.id);
    }
    return out;
}

/** Der Faden EINES Ziels. Für viele Ziele auf einmal: {@link kommentareJeZiel}. */
export function kommentareZu(alle: BlockNotiz[], ziel: KommentarZiel): Kommentar[] {
    return kommentareJeZiel(alle).get(zielSchluessel(ziel)) ?? [];
}

/**
 * Welche Notizen NICHT mehr als eigener Block im Strom stehen, weil sie hinter
 * einer Sprechblase wieder auftauchen. Geliefert werden Notiz-Schlüssel
 * (`quelle:id`).
 *
 * ══ NICHTS DARF VERSCHWINDEN ═══════════════════════════════════════════════
 * Versteckt wird nur, was auch wieder zu öffnen ist. Drei Fälle bleiben
 * deshalb ausdrücklich Blöcke:
 *
 *  · Das Elternteil ist WEG. `VorgangNoteService.remove` löscht beim
 *    Leerräumen eines Blocks ohne seine Antworten (`mitAntworten: false`) —
 *    die Kommentare bleiben elternlos zurück. Als Block sind sie weiter
 *    lesbar, hinter einer Sprechblase, die es nicht gibt, wären sie für immer
 *    unerreichbar.
 *  · Das Elternteil ist INHALTSLEER. Solche Zeilen aus der Zeit vor dem
 *    13.09.2026 zeichnet `bloeckeAus` nicht mehr — es gäbe also keinen Kopf,
 *    an dem die Sprechblase hinge.
 *  · Das Elternteil ist selbst ein KOMMENTAR (Antworten aus dem V2-Chat). Ein
 *    Faden am Faden wird nirgends gezeichnet.
 *
 * `ereignisZiele` sind die Schlüssel der Mails, Anrufe und Dokumente, die auf
 * DIESER Seite stehen (`mail:7`, `anruf:8`, `dokument:9`) — nur der Aufrufer
 * weiß, was er zeichnet. Ein Kommentar an einer Mail, die hier gar nicht
 * vorkommt, bleibt Block.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function versteckteKommentare(
    alle: BlockNotiz[],
    ereignisZiele: Set<string>,
): Set<string> {
    const gezeichneteBloecke = new Set<string>();
    for (const n of alle) {
        if (istLeererBlocktext(n.text)) continue;
        if (istKommentar(n)) continue;
        gezeichneteBloecke.add(notizSchluessel(n));
    }

    const out = new Set<string>();
    for (const n of alle) {
        const ziel = zielVon(n);
        if (!ziel) continue;
        const erreichbar =
            ziel.art === "notiz"
                ? gezeichneteBloecke.has(`${ziel.quelle ?? "vorgang"}:${ziel.id}`)
                : ereignisZiele.has(zielSchluessel(ziel));
        if (erreichbar) out.add(notizSchluessel(n));
    }
    return out;
}

/**
 * Der Faden an der SPRECHBLASE eines Abschnitts: ohne die Kommentare, deren
 * markierte Stelle noch im Text steht — die zeigt die Stelle selbst.
 *
 * Bis zum 28.09.2026 standen sie an beiden Orten: an der Stelle UND in der
 * Zahl am Rand. Eine Stelle, deren Markierung gelöscht wurde, fällt dagegen
 * hierher zurück (Plan 20.09.2026, Paket 5: „Wird die markierte Stelle
 * gelöscht, bleibt der Faden und rutscht an die Sprechblase des Abschnitts")
 * — sonst wäre sie nirgends mehr zu erreichen.
 */
export function randFaden(faden: Kommentar[], abschnittHtml: string): Kommentar[] {
    return faden.filter((k) => !k.anker || !abschnittHtml.includes(`data-anker="${k.anker}"`));
}

/**
 * Kommentare an einer ausgeblendeten Postfachkopie am VERTRETER zeigen (T-11).
 *
 * Dieselbe Mail aus zwei Postfächern steht im Vorgang als EINE Karte
 * (`gruppiereVorgangsMails`). Ein Kommentar hängt aber per `mailLinkId` an
 * genau einer Zeile — hing er an der ausgeblendeten Kopie, stünde er sonst als
 * freier Block im Notizstrom, und die Fäden beider Kopien wären getrennt.
 *
 * NUR für die Anzeige: Geschrieben wird nichts, Bearbeiten und Löschen laufen
 * weiter über die Notiz-`id`. Ist nichts umzuschlüsseln, kommt DASSELBE Array
 * zurück (Memo-stabil).
 */
export function kommentareAufVertreter<T extends { mailLinkId?: number | null }>(
    notizen: T[],
    vertreterVon: ReadonlyMap<number, number>,
): T[] {
    let geaendert = false;
    const out = notizen.map((n) => {
        if (n.mailLinkId == null) return n;
        const v = vertreterVon.get(n.mailLinkId);
        if (v == null || v === n.mailLinkId) return n;
        geaendert = true;
        return { ...n, mailLinkId: v };
    });
    return geaendert ? out : notizen;
}
